import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
  TablePagination
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  MailOutline as MailOutlineIcon
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useSettings } from '../../contexts/SettingsContext';
import { usePermission } from '../../hooks/usePermission';

const DEFAULT_DEPARTMENTS = [
  { value: 'SOC', label: 'SOC' },
  { value: 'Incident_Response', label: 'Incident Response' },
  { value: 'Threat_Intelligence', label: 'Threat Intelligence' },
  { value: 'Forensics', label: 'Forensics' },
  { value: 'Management', label: 'Management' },
  { value: 'IT_Security', label: 'IT Security' },
  { value: 'Compliance', label: 'Compliance' }
];

const EMPTY_INVITE = { email: '', firstName: '', lastName: '', role: 'analyst', department: 'SOC' };

/** Prefer the server's message (including express-validator errors) over a generic one. */
const serverMessage = (err, fallback) =>
  err?.response?.data?.message
  || err?.response?.data?.errors?.[0]?.msg
  || err?.message
  || fallback;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    try {
      return document.execCommand('copy');
    } catch (err) {
      return false;
    } finally {
      document.body.removeChild(el);
    }
  }
};

const userId = (u) => u?._id || u?.id;

const UserManagement = () => {
  const { getFormattedDepartments } = useSettings();
  const { canManageUsers } = usePermission();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Roles come straight from GET /settings/roles so custom roles appear too
  const [roles, setRoles] = useState([]);
  const [rolesError, setRolesError] = useState(null);

  // Pending invitations
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  // Dialogs
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteAction, setDeleteAction] = useState('deactivate');
  const [linkDialog, setLinkDialog] = useState(null); // { title, email, link, message }
  const [copied, setCopied] = useState(false);

  // Actions menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  // Forms
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', role: '', department: '', isActive: true });

  // Filters / pagination (search is debounced)
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  const settingsDepartments = getFormattedDepartments();
  const departments = settingsDepartments.length ? settingsDepartments : DEFAULT_DEPARTMENTS;

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const loadRoles = useCallback(async () => {
    try {
      const response = await apiService.settings.getRoles();
      setRoles(Array.isArray(response.data) ? response.data : []);
      setRolesError(null);
    } catch (err) {
      setRolesError(serverMessage(err, 'Failed to load roles'));
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (searchTerm) params.search = searchTerm;
      if (roleFilter) params.role = roleFilter;
      if (departmentFilter) params.department = departmentFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await apiService.users.getAll(params);
      setUsers(response.data.users || []);
      setTotalUsers(response.data.pagination?.total || 0);
      setError(null);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, roleFilter, departmentFilter, statusFilter]);

  const loadInvitations = useCallback(async () => {
    if (!canManageUsers) return;
    try {
      setInvitationsLoading(true);
      const response = await apiService.invitations.getPending();
      setInvitations(response.data.invitations || []);
      setInvitationsError(null);
    } catch (err) {
      setInvitationsError(serverMessage(err, 'Failed to load pending invitations'));
    } finally {
      setInvitationsLoading(false);
    }
  }, [canManageUsers]);

  useEffect(() => { loadRoles(); }, [loadRoles]);
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadInvitations(); }, [loadInvitations]);

  const refreshAll = () => {
    loadUsers();
    loadInvitations();
  };

  const showLink = (title, email, link, message) => {
    setCopied(false);
    setLinkDialog({ title, email, link, message });
  };

  const handleCopy = async (text) => {
    const ok = await copyText(text);
    setCopied(ok);
    if (ok) setSuccess('Invitation link copied to clipboard');
    else setError('Could not copy automatically; select the link and copy it manually.');
  };

  const handleInviteUser = async () => {
    try {
      setBusy(true);
      setError(null);
      const { data } = await apiService.users.invite(inviteForm);
      setInviteDialogOpen(false);
      setInviteForm(EMPTY_INVITE);
      if (data.emailSent === false && data.invitationLink) {
        showLink('Invitation created - email not sent', inviteForm.email, data.invitationLink, data.message);
      } else {
        setSuccess(data.message || 'User invitation sent successfully');
      }
      refreshAll();
    } catch (err) {
      setError(serverMessage(err, 'Failed to send invitation'));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async (invitation) => {
    try {
      setBusy(true);
      setError(null);
      const { data } = await apiService.invitations.resend(userId(invitation));
      if (data.emailSent === false && data.invitationLink) {
        showLink('Invitation renewed - email not sent', invitation.email, data.invitationLink, data.message);
      } else {
        setSuccess(data.message || 'Invitation re-sent');
      }
      loadInvitations();
    } catch (err) {
      setError(serverMessage(err, 'Failed to resend invitation'));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setBusy(true);
      setError(null);
      const { data } = await apiService.invitations.revoke(userId(revokeTarget));
      setSuccess(data.message || 'Invitation cancelled');
      setRevokeTarget(null);
      refreshAll();
    } catch (err) {
      setError(serverMessage(err, 'Failed to revoke invitation'));
    } finally {
      setBusy(false);
    }
  };

  const handleEditUser = async () => {
    try {
      setBusy(true);
      setError(null);
      const { data } = await apiService.users.update(userId(selectedUser), editForm);
      setSuccess(data.message || 'User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      setError(serverMessage(err, 'Failed to update user'));
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivateUser = async () => {
    try {
      setBusy(true);
      setError(null);
      const { data } = await apiService.users.deactivate(userId(selectedUser));
      setSuccess(data.message || 'User deactivated successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      setError(serverMessage(err, 'Failed to deactivate user'));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setBusy(true);
      setError(null);
      const { data } = await apiService.users.delete(userId(selectedUser));
      setSuccess(data.message || 'User deleted permanently');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      refreshAll();
    } catch (err) {
      setError(serverMessage(err, 'Failed to delete user'));
    } finally {
      setBusy(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role || '',
      department: user.department || '',
      isActive: !!user.isActive
    });
    setEditDialogOpen(true);
    setAnchorEl(null);
  };

  const openDeactivateDialog = (user) => {
    setSelectedUser(user);
    setDeleteAction('deactivate');
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteAction('delete');
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const getStatusChip = (user) => {
    if (user.invitationToken || (user.invitationExpires && !user.isActive)) {
      return <Chip label="Pending" color="warning" size="small" />;
    }
    return user.isActive
      ? <Chip label="Active" color="success" size="small" />
      : <Chip label="Inactive" color="error" size="small" />;
  };

  const getRoleLabel = (role) => roles.find(r => r.name === role)?.displayName || role;
  const getDepartmentLabel = (department) => departments.find(d => d.value === department)?.label || department;
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');

  const roleMenuItems = roles.map(role => (
    <MenuItem key={role.name} value={role.name}>
      {role.displayName || role.name}
      {role.isSystemRole && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>(built-in)</Typography>
      )}
    </MenuItem>
  ));

  return (
    <Box>
      <Card>
        <CardHeader
          title="User Management"
          subheader="Manage users, roles, and permissions for your organization"
          action={
            <Box display="flex" gap={1}>
              <Button startIcon={<RefreshIcon />} onClick={refreshAll} disabled={loading || busy}>
                Refresh
              </Button>
              {canManageUsers && (
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => { setError(null); setInviteDialogOpen(true); }}
                >
                  Invite User
                </Button>
              )}
            </Box>
          }
        />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
          {rolesError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRolesError(null)}>
              Roles could not be loaded ({rolesError}). Role names are shown as stored.
            </Alert>
          )}
          {!canManageUsers && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have read-only access to users. Inviting, editing and deactivating users requires the
              user management permission.
            </Alert>
          )}

          {/* Filters */}
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <TextField
              placeholder="Search users..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Role</InputLabel>
              <Select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }} label="Role">
                <MenuItem value="">All Roles</MenuItem>
                {roles.map(role => (
                  <MenuItem key={role.name} value={role.name}>{role.displayName || role.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Department</InputLabel>
              <Select value={departmentFilter} onChange={(e) => { setDepartmentFilter(e.target.value); setPage(0); }} label="Department">
                <MenuItem value="">All Departments</MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept.value} value={dept.value}>{dept.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} label="Status">
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Users table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  {canManageUsers && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center"><CircularProgress /></TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={userId(user)}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar>{user.firstName?.[0]}{user.lastName?.[0]}</Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {user.firstName} {user.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{getRoleLabel(user.role)}</TableCell>
                      <TableCell>{getDepartmentLabel(user.department)}</TableCell>
                      <TableCell>{getStatusChip(user)}</TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      {canManageUsers && (
                        <TableCell align="right">
                          <IconButton onClick={(e) => handleMenuClick(e, user)} size="small">
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      {/* Pending invitations */}
      {canManageUsers && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            avatar={<MailOutlineIcon />}
            title="Pending Invitations"
            subheader="Users who have been invited but have not finished setting up their account"
            action={
              <Button startIcon={<RefreshIcon />} onClick={loadInvitations} disabled={invitationsLoading || busy}>
                Refresh
              </Button>
            }
          />
          <CardContent>
            {invitationsError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInvitationsError(null)}>
                {invitationsError}
              </Alert>
            )}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invitee</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Invited by</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitationsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
                    </TableRow>
                  ) : invitations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">No pending invitations</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invitations.map((inv) => (
                      <TableRow key={userId(inv)}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{inv.firstName} {inv.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary">{inv.email}</Typography>
                        </TableCell>
                        <TableCell>{getRoleLabel(inv.role)}</TableCell>
                        <TableCell>{getDepartmentLabel(inv.department)}</TableCell>
                        <TableCell>
                          {inv.invitedBy
                            ? `${inv.invitedBy.firstName || ''} ${inv.invitedBy.lastName || ''}`.trim() || inv.invitedBy.email
                            : '-'}
                        </TableCell>
                        <TableCell>{formatDate(inv.invitationExpires)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={inv.invitationLink ? 'Copy invitation link' : 'Link unavailable'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!inv.invitationLink}
                                onClick={() => handleCopy(inv.invitationLink)}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Resend (issues a fresh link valid for 7 days)">
                            <span>
                              <IconButton size="small" disabled={busy} onClick={() => handleResend(inv)}>
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Revoke invitation">
                            <span>
                              <IconButton size="small" color="error" disabled={busy} onClick={() => setRevokeTarget(inv)}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* User actions menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => openEditDialog(menuUser)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => openDeactivateDialog(menuUser)} sx={{ color: 'warning.main' }}>
          <ListItemIcon><BlockIcon fontSize="small" color="warning" /></ListItemIcon>
          <ListItemText>Deactivate User</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openDeleteDialog(menuUser)} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      {/* Invite dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Email Address"
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><EmailIcon /></InputAdornment>
                ),
              }}
            />
            <Box display="flex" gap={2}>
              <TextField
                label="First Name"
                value={inviteForm.firstName}
                onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={inviteForm.lastName}
                onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                fullWidth
                required
              />
            </Box>
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  label="Role"
                >
                  {roleMenuItems}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={inviteForm.department}
                  onChange={(e) => setInviteForm({ ...inviteForm, department: e.target.value })}
                  label="Department"
                >
                  {departments.map(dept => (
                    <MenuItem key={dept.value} value={dept.value}>{dept.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Typography variant="caption" color="text.secondary">
              The invitee receives a link to set their password (valid 7 days). If email delivery is not
              configured, the link is shown here for you to share.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleInviteUser}
            variant="contained"
            disabled={busy || !inviteForm.email || !inviteForm.firstName || !inviteForm.lastName || !inviteForm.role}
          >
            {busy ? <CircularProgress size={20} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invitation link dialog (shown when email delivery is not configured) */}
      <Dialog open={Boolean(linkDialog)} onClose={() => setLinkDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{linkDialog?.title || 'Invitation link'}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {linkDialog?.message || 'Email delivery is not configured. Copy this link and send it to the user.'}
          </Alert>
          <DialogContentText sx={{ mb: 1 }}>
            Invitation for <strong>{linkDialog?.email}</strong>:
          </DialogContentText>
          <TextField
            fullWidth
            value={linkDialog?.link || ''}
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={() => handleCopy(linkDialog?.link)}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="First Name"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                fullWidth
                required
              />
            </Box>
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  label="Role"
                >
                  {roleMenuItems}
                  {editForm.role && !roles.some(r => r.name === editForm.role) && (
                    <MenuItem value={editForm.role}>{editForm.role}</MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={editForm.department}
                  onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                  label="Department"
                >
                  {departments.map(dept => (
                    <MenuItem key={dept.value} value={dept.value}>{dept.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.isActive}
                onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value })}
                label="Status"
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained" disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate / delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{deleteAction === 'deactivate' ? 'Deactivate User' : 'Delete User'}</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteAction === 'deactivate'
              ? `Are you sure you want to deactivate ${selectedUser?.firstName} ${selectedUser?.lastName}? They will no longer be able to sign in, but their data will be preserved.`
              : `Are you sure you want to permanently delete ${selectedUser?.firstName} ${selectedUser?.lastName}? This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={deleteAction === 'deactivate' ? handleDeactivateUser : handleDeleteUser}
            color="error"
            variant="contained"
            disabled={busy}
          >
            {busy ? <CircularProgress size={20} /> : (deleteAction === 'deactivate' ? 'Deactivate' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke invitation confirmation */}
      <Dialog open={Boolean(revokeTarget)} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoke Invitation</DialogTitle>
        <DialogContent>
          <Typography>
            Revoke the invitation for {revokeTarget?.email}? The link will stop working and the pending
            account will be removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button onClick={handleRevoke} color="error" variant="contained" disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
